import { useMemo, useRef } from 'react'

import { Slider } from 'components/ui/Slider'
import useSlidesToShow from 'hooks/useSlidesToShow'
import { useIsMobile } from 'hooks/useIsMobile'
import { COURSE_VERTICAL_CARD_SLIDER_MARGIN } from 'components/ui/CourseCards/components/molecules/CourseVerticalCard/constants'
import { LearningPathCard } from 'components/ui/LearningPathCard'
import { ILearningPathCard } from 'models/learningPathCard.model'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import styles from './LearningPathSlider.module.scss'

interface Props {
  learningPaths: ILearningPathCard[]
}

const siderWidth = 200

export const LearningPathSlider = ({ learningPaths }: Props) => {
  const swiping = useRef(false)
  const windowsSize = useWindowSize()
  const { width } = windowsSize
  const isSiderWidth = width ? width < mobileWidth + siderWidth : false
  const isMobile = useIsMobile()

  const sliderContainerRef = useRef<HTMLDivElement>(null)

  const cardWidth = Math.min(
    900,
    (sliderContainerRef.current?.offsetWidth || 0) - 40
  )

  const slidesToShow = useSlidesToShow({
    containerRef: sliderContainerRef,
    slideWidth: cardWidth,
  })

  const learningPathCardWidth = useMemo(() => {
    if (isSiderWidth) return isMobile ? 280 : cardWidth + siderWidth
    return Math.max(690, cardWidth)
  }, [isSiderWidth, isMobile, cardWidth])

  return (
    <div ref={sliderContainerRef} className={styles.sliderContainer}>
      <Slider
        afterChange={() => {
          swiping.current = false
        }}
        beforeChange={() => {
          swiping.current = true
        }}
        dots={false}
        slidesToScroll={1}
        centerMode={false}
        variableWidth
        arrows={!isMobile && learningPaths.length > slidesToShow}
        infinite={learningPaths.length > slidesToShow}
        swipe={learningPaths.length > slidesToShow}
      >
        {learningPaths.map((learningPath) => (
          <div key={learningPath.id}>
            {/* Is necessary to wrap the card with a div to avoid a bug with the margin */}
            <div
              style={{
                marginRight: COURSE_VERTICAL_CARD_SLIDER_MARGIN,
                marginBottom: 10,
              }}
            >
              <LearningPathCard
                learningPath={learningPath}
                className={styles.card}
                style={{
                  width: learningPathCardWidth,
                }}
              />
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}
